import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Input, Select, Tabs } from 'antd';
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { login } from '../api/appApi';
import { Icon, ORDERS_ICON } from '../components/Icon';
import badge from '../assets/brand/uncle-advertisers-badge.svg';
import { toast } from '../lib/toast';
import { fetchPortalDepartments, portalLogin } from '../zen/api';

export function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialPortal = new URLSearchParams(location.search).get('tab') === 'portal';
  const [tab, setTab] = useState<'login' | 'portal'>(initialPortal ? 'portal' : 'login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [portalDepartmentIds, setPortalDepartmentIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const departments = useQuery({
    queryKey: ['zen', 'portal', 'departments'],
    queryFn: fetchPortalDepartments,
    enabled: tab === 'portal',
    staleTime: 60_000,
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (tab === 'login') {
        const ok = await login(username.trim(), password);
        if (!ok) throw new Error('Invalid username or password');
        navigate('/');
      } else {
        if (!portalDepartmentIds.length) throw new Error('Select at least one department');
        await portalLogin(username.trim(), password, portalDepartmentIds);
        navigate('/portal');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left panel */}
      <div className="login-left">
        <div className="login-brand">
          <img src={badge} alt="Uncle Advertisers" className="login-brand-badge" />
          <div className="login-brand-copy">
            <div className="ua-overline" style={{ color: 'var(--ua-chrome-text)', marginBottom: 12 }}>Order management</div>
            <h1>Every job, from first call to final fitting.</h1>
            <p>Work orders, shared department queues and customer ledgers for print, signage and large-format.</p>
          </div>

          <div className="login-features">
            <div className="login-feature">
              <div className="login-feature-icon">
                <Icon d={ORDERS_ICON} size={18} />
              </div>
              <div className="login-feature-text">
                <strong>One work order per job</strong>
                <span>From order receipt through delivery</span>
              </div>
            </div>
            <div className="login-feature">
              <div className="login-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <div className="login-feature-text">
                <strong>Shared department queues</strong>
                <span>Workers pick up jobs themselves</span>
              </div>
            </div>
            <div className="login-feature">
              <div className="login-feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              </div>
              <div className="login-feature-text">
                <strong>Accounts and payments</strong>
                <span>Invoices and customer ledgers</span>
              </div>
            </div>
          </div>
        </div>
        <div className="login-signpost" aria-hidden="true" />
      </div>

      {/* Right panel — form */}
      <div className="login-right">
        <div className="login-form-wrap">
          <img src={badge} alt="Uncle Advertisers" className="login-mobile-brand" />
          <div className="login-form-heading">
            <h2>{tab === 'login' ? 'Welcome back' : 'View-only portal'}</h2>
            <p>
              {tab === 'login'
                ? 'Sign in to your account to continue'
                : 'Sign in as admin and choose which department queues to display'}
            </p>
          </div>

          <Tabs
            activeKey={tab}
            onChange={(key) => setTab(key as 'login' | 'portal')}
            items={[
              { key: 'login', label: 'Login' },
              { key: 'portal', label: 'View-only portal' },
            ]}
          />

          <form onSubmit={submit}>
            <div className="login-form-fields">
              <div className="form-group">
                <label htmlFor="username">{tab === 'login' ? 'Username' : 'Admin username'}</label>
                <Input
                  id="username"
                  size="large"
                  prefix={<UserOutlined />}
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  placeholder="e.g. admin"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <Input.Password
                  id="password"
                  size="large"
                  prefix={<LockOutlined />}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  placeholder="Password"
                />
              </div>
              {tab === 'portal' && (
                <div className="form-group">
                  <label htmlFor="portal-departments">Departments to display</label>
                  <Select
                    id="portal-departments"
                    mode="multiple"
                    value={portalDepartmentIds}
                    onChange={setPortalDepartmentIds}
                    loading={departments.isLoading}
                    placeholder="Select one or more departments"
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    options={(departments.data ?? []).map((department) => ({
                      value: department.id,
                      label: department.name,
                    }))}
                  />
                </div>
              )}
            </div>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              style={{ width: '100%' }}
              loading={busy}
              disabled={busy}
            >
              {tab === 'login' ? 'Sign in' : 'Open view-only board'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
